import { useState, useEffect, useRef, useCallback } from "react";
import { conversationApi, chatbotApi, formatApiError } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Loader2, MessageCircle, User, Bot, Send, PhoneOff, Phone, RefreshCw, Circle, Volume2, VolumeX } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

const MarkdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm">{children}</li>,
  code: ({ inline, children }) => inline ? <code className="bg-black/10 px-1 text-xs font-mono">{children}</code> : <code className="block bg-black/10 p-2 text-xs font-mono my-2">{children}</code>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="underline">{children}</a>,
};

const POLL_INTERVAL = 5000;

export default function LiveChat() {
  const [chatbots, setChatbots] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [filterBot, setFilterBot] = useState("all");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [polling, setPolling] = useState(true);
  const messagesEndRef = useRef(null);
  const prevConvCountRef = useRef(0);
  const selectedConvRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling for new conversations and messages
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const convsRes = await conversationApi.list(filterBot === "all" ? undefined : filterBot);
        const newConvs = convsRes.data;
        
        // Notify on new conversations
        if (prevConvCountRef.current > 0 && newConvs.length > prevConvCountRef.current && soundEnabled) {
          playNotificationSound();
        }
        prevConvCountRef.current = newConvs.length;
        setConversations(newConvs);
        
        // Auto-refresh messages for selected conversation
        if (selectedConvRef.current) {
          const msgsRes = await conversationApi.getMessages(selectedConvRef.current.id);
          setMessages(msgsRes.data);
        }
      } catch (e) {
        console.error("Poll failed:", e.message);
      }
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, filterBot, soundEnabled]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      gain.gain.value = 0.1;
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {
      console.error("Notification sound failed:", e.message);
    }
  };

  const loadData = async () => {
    try {
      const [botsRes, convsRes] = await Promise.all([
        chatbotApi.list(),
        conversationApi.list()
      ]);
      setChatbots(botsRes.data);
      setConversations(convsRes.data);
      prevConvCountRef.current = convsRes.data.length;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId) => {
    try {
      const res = await conversationApi.getMessages(convId);
      setMessages(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleSelectConv = (conv) => {
    setSelectedConv(conv);
    loadMessages(conv.id);
  };

  const handleTakeover = async () => {
    if (!selectedConv) return;
    try {
      await conversationApi.takeover(selectedConv.id);
      const updated = { ...selectedConv, is_live_chat_active: true };
      setSelectedConv(updated);
      setConversations(prev => prev.map(c => c.id === selectedConv.id ? updated : c));
      toast.success("Live chat takeover activated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRelease = async () => {
    if (!selectedConv) return;
    try {
      await conversationApi.release(selectedConv.id);
      const updated = { ...selectedConv, is_live_chat_active: false };
      setSelectedConv(updated);
      setConversations(prev => prev.map(c => c.id === selectedConv.id ? updated : c));
      toast.success("Released back to AI");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleSendReply = async () => {
    if (!selectedConv || !replyText.trim()) return;
    setSending(true);
    try {
      const res = await conversationApi.reply(selectedConv.id, replyText);
      setMessages(prev => [...prev, res.data]);
      setReplyText("");
    } catch (e) {
      toast.error(e.response?.status === 422
        ? "Message is too long. Please keep it under 8,000 characters."
        : formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = filterBot === "all"
    ? conversations
    : conversations.filter(c => c.chatbot_id === filterBot);

  const getRolePrefix = (role) => {
    if (role === "user") return "Visitor: ";
    if (role === "agent") return "Agent: ";
    return "Bot: ";
  };

  const getRoleIcon = (role) => {
    if (role === "user") return <User className="w-3 h-3" />;
    if (role === "agent") return <User className="w-3 h-3" />;
    return <Bot className="w-3 h-3" />;
  };

  const getRoleLabel = (msg) => {
    if (msg.role === "agent") return msg.agent_name || "Agent";
    if (msg.role === "user") return "Visitor";
    return "AI Bot";
  };

  const activeConversations = filteredConversations.filter(c => c.is_live_chat_active);
  const recentConversations = filteredConversations.filter(c => !c.is_live_chat_active);
  const getChatbotName = (id) => chatbots.find(b => b.id === id)?.name || "Unknown";

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const ConversationItem = ({ conv }) => (
    <div
      onClick={() => handleSelectConv(conv)}
      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${selectedConv?.id === conv.id ? 'bg-muted border-l-2 border-l-primary' : ''}`}
      data-testid={`conversation-${conv.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="font-medium text-sm truncate flex-1">{getChatbotName(conv.chatbot_id)}</p>
        <div className="flex items-center gap-1.5">
          {conv.is_live_chat_active && (
            <Badge variant="outline" className="bg-[hsl(var(--success-green))] text-white border-0 text-[10px] px-1.5 py-0">
              LIVE
            </Badge>
          )}
          <span className="text-[10px] text-muted-foreground">{timeAgo(conv.last_message_at || conv.created_at)}</span>
        </div>
      </div>
      {conv.last_message && (
        <p className="text-xs text-muted-foreground truncate">
          {getRolePrefix(conv.last_message_role)}
          {conv.last_message}
        </p>
      )}
      <p className="text-[10px] text-muted-foreground mt-1">
        {conv.message_count} messages
      </p>
    </div>
  );

  return (
    <div className="h-[calc(100vh-180px)]" data-testid="livechat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <PageHeader title="Live Chat" subtitle="Take over chats from your AI agent the moment a customer needs a human." /><p className="text-muted-foreground mt-1 flex items-center gap-2">
            Monitor and respond to conversations
            {polling && <Circle className="w-2 h-2 fill-[hsl(var(--success-green))] text-[hsl(var(--success-green))]" />}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="h-8 w-8"
            title={soundEnabled ? "Mute notifications" : "Enable notifications"}
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={loadData} className="rounded-none" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-px bg-border h-[calc(100%-60px)] border border-border">
        {/* Conversations List */}
        <div className="col-span-4 bg-background flex flex-col">
          <div className="p-3 border-b border-border space-y-2">
            <Select value={filterBot} onValueChange={setFilterBot}>
              <SelectTrigger className="rounded-none h-9">
                <SelectValue placeholder="Filter by chatbot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Chatbots</SelectItem>
                {chatbots.map(bot => (
                  <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 text-xs text-muted-foreground">
              <span>{filteredConversations.length} conversations</span>
              {activeConversations.length > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[hsl(var(--success-green))]/10 text-[hsl(var(--success-green))] border-[hsl(var(--success-green))]/30">
                  {activeConversations.length} live
                </Badge>
              )}
            </div>
          </div>
          <ScrollArea className="flex-1">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <p className="text-xs mt-1">Conversations will appear here when visitors chat</p>
              </div>
            ) : (
              <div>
                {/* Active Live Chats */}
                {activeConversations.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-[hsl(var(--success-green))]/5 border-b border-border">
                      <p className="text-xs font-semibold text-[hsl(var(--success-green))] uppercase tracking-wider">Active Live Chats</p>
                    </div>
                    <div className="divide-y divide-border">
                      {activeConversations.map(conv => (
                        <ConversationItem key={conv.id} conv={conv} />
                      ))}
                    </div>
                  </>
                )}
                {/* Recent Conversations */}
                {recentConversations.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-muted/30 border-b border-border">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent</p>
                    </div>
                    <div className="divide-y divide-border">
                      {recentConversations.map(conv => (
                        <ConversationItem key={conv.id} conv={conv} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="col-span-8 bg-background flex flex-col">
          {selectedConv ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-border flex items-center justify-between bg-background">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{getChatbotName(selectedConv.chatbot_id)}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      Session: {selectedConv.session_id?.substring(0, 12)}...
                      {selectedConv.is_live_chat_active && (
                        <span className="flex items-center gap-1 text-[hsl(var(--success-green))]">
                          <Circle className="w-1.5 h-1.5 fill-current" /> Live
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedConv.is_live_chat_active ? (
                    <Button variant="outline" onClick={handleRelease} className="rounded-none h-8 text-xs" data-testid="release-btn">
                      <PhoneOff className="w-3 h-3 mr-1.5" /> Release to AI
                    </Button>
                  ) : (
                    <Button onClick={handleTakeover} className="rounded-none h-8 text-xs" data-testid="takeover-btn">
                      <Phone className="w-3 h-3 mr-1.5" /> Take Over
                    </Button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        msg.role === 'user' 
                          ? 'bg-primary text-primary-foreground' 
                          : msg.role === 'agent' 
                            ? 'bg-[hsl(var(--klein-blue))] text-white' 
                            : 'bg-muted'
                      } p-3 rounded-lg`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          {getRoleIcon(msg.role)}
                          <span className="text-[10px] font-medium uppercase tracking-wide">
                            {getRoleLabel(msg)}
                          </span>
                        </div>
                        {msg.role === 'assistant' ? (
                          <div className="text-sm">
                            <ReactMarkdown components={MarkdownComponents}>{msg.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        )}
                        <p className="text-[10px] opacity-60 mt-1">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Reply Input - always visible when taken over */}
              {selectedConv.is_live_chat_active ? (
                <div className="p-3 border-t border-border bg-background">
                  <div className="flex gap-2">
                    <Input
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      maxLength={8000}
                      placeholder="Type your reply as agent..."
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                      className="rounded-none"
                      data-testid="reply-input"
                    />
                    <Button onClick={handleSendReply} disabled={sending || !replyText.trim()} className="rounded-none" data-testid="send-reply-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  {replyText.length > 7500 && (
                    <p className="text-xs text-muted-foreground mt-1 text-right" data-testid="reply-input-count">
                      {replyText.length.toLocaleString()} / 8,000
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-3 border-t border-border bg-muted/30 text-center">
                  <p className="text-xs text-muted-foreground">Click "Take Over" to respond as a human agent</p>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">Select a conversation</p>
                <p className="text-sm mt-1">Choose a conversation from the left to view and respond</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
